/*
  Custom dark dropdown that DECORATES an existing native <select>.

  Native <select> option popups are drawn by the browser/OS and can't be reliably
  themed (they render light even with color-scheme: dark on some browsers). So instead
  of restyling the select, we hide it (it stays in the DOM — form submission and every
  existing change-listener keep working) and render our own dark list beside it. On
  pick we write the value back to the native select and fire input+change, so Dawn's
  variant picker, the collection sort and the customization form react exactly as if
  the native control changed.

  Enhances the selects that need it (customization form + collection/search sort) and
  re-enhances after AJAX/section swaps (facets re-render the sort on filter changes).
*/
(function () {
  'use strict';

  var SELECTORS =
    '.cf-select__select, .cf-customization .select__select, .facet-filters__sort, #SortBy-mobile';

  function enhance(select) {
    if (!(select instanceof HTMLSelectElement) || select.multiple) return;
    var host = select.closest('.cf-select, .select') || select.parentElement;
    if (!host || host.classList.contains('custom-select-host')) return;
    host.classList.add('custom-select-host');

    var ui = document.createElement('div');
    ui.className = 'custom-select';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML =
      '<span class="custom-select__label"></span><span class="custom-select__caret" aria-hidden="true"></span>';
    var labelEl = button.querySelector('.custom-select__label');

    var list = document.createElement('ul');
    list.className = 'custom-select__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    function currentText() {
      var opt = select.options[select.selectedIndex];
      return opt ? opt.textContent.trim() : '';
    }

    function rebuild() {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (opt) {
        var li = document.createElement('li');
        li.className = 'custom-select__option';
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        li.textContent = opt.textContent.trim();
        if (opt.disabled) li.setAttribute('aria-disabled', 'true');
        if (opt.selected) li.setAttribute('aria-selected', 'true');
        list.appendChild(li);
      });
      labelEl.textContent = currentText();
      button.disabled = select.disabled;
    }

    function open() {
      if (select.disabled) return;
      rebuild();
      list.hidden = false;
      button.setAttribute('aria-expanded', 'true');
    }
    function close() {
      list.hidden = true;
      button.setAttribute('aria-expanded', 'false');
    }

    button.addEventListener('click', function (e) {
      e.preventDefault();
      if (list.hidden) open();
      else close();
    });
    button.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    list.addEventListener('click', function (e) {
      var li = e.target.closest('.custom-select__option');
      if (!li || li.getAttribute('aria-disabled') === 'true') return;
      close();
      var val = li.dataset.value;
      if (select.value !== val) {
        select.value = val;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      labelEl.textContent = li.textContent;
    });

    document.addEventListener('click', function (e) {
      if (!host.contains(e.target)) close();
    });

    // Reflect changes made elsewhere (variant JS reselecting, product-customization
    // enabling/disabling the field) so the button label + disabled state stay right.
    select.addEventListener('change', rebuild);
    new MutationObserver(rebuild).observe(select, {
      attributes: true,
      attributeFilter: ['disabled'],
    });

    ui.appendChild(button);
    ui.appendChild(list);
    host.appendChild(ui);
    rebuild();
  }

  function enhanceAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTORS).forEach(enhance);
    if (root && root.matches && root.matches(SELECTORS)) enhance(root);
  }

  function init() {
    enhanceAll(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    enhanceAll(e.target);
  });

  // Facets replace the sort select via AJAX on filter changes — re-enhance the new
  // one. Debounced to one pass per frame so it stays cheap.
  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      enhanceAll(document);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
